// icons
import { Icon, IconProps } from '@iconify/react'
// @mui
import { Box, BoxProps, SxProps, Theme } from '@mui/material'
import {
  DEFAULT_ICON_SIZE,
  IconSizeName,
  resolveIconGeometry,
} from 'src/design-system'
import {
  BEAST_GLYPH_VIEW_BOX,
  beastGlyphBody,
  isBeastGlyph,
} from 'src/design-system/glyphs.generated'

// ----------------------------------------------------------------------
// Single entry point for every icon in the app.
//
// Three packs, one grid. Lucide (`lucide:`) draws the UI, Tabler (`tabler:`)
// draws the vehicle and EV glyphs, and Beast (`beast:`) draws the marks
// neither pack can carry; all three are line families on a 24 unit grid with a
// 2 unit stroke, rendered in `currentColor`, so a glyph inherits the colour of
// the text beside it. The one filled exception is the Rentals mark
// (`mdi:car-multiple`), see `design-system/icons.ts`.
//
// The Beast pack is drawn here rather than fetched, and that is the only
// difference between it and the other two: its bodies are compiled in from
// `design-system/glyphs/*.svg` and written straight onto an svg with the
// attributes `@iconify/react` would have put there. A glyph the Iconify API
// does not return renders as nothing at all and nothing reports it, which is
// not a risk worth taking for a mark on a control that opens a car. Geometry,
// size and stroke are identical on both paths, because they are the same
// arithmetic: the body is the same stroked group a pack body arrives in.
//
// Size is a name, not a number. The five optical steps (`chip` 14, `dense`
// 16, `control` 18, `toolbar` 20, `action` 24) are chosen by the context a glyph
// sits in, never by eye, and each carries its own rendered stroke weight. An
// svg stroke scales with its box, so a 14px glyph drawn straight from the pack
// puts down 1.17 device pixels where a 24px one puts down 2. That drift is what
// makes a toolbar look hand assembled. The wrapper writes a `stroke-width` onto
// every element inside the svg, scaled by grid over size, so the weight on
// screen is the token's (1.35 at 14 down to 1.2 at 20 and 24), whatever the
// size. The ramp is not flat: below 20px a glyph loses more apparent weight
// than its stroke loses width, so the small steps are topped up.
//
// A glyph that is the only content of its control takes `solo`, the heavier
// weight that keeps a dark mark on a light fill reading as equal to a light
// mark on ink.

// Size is the box; a few glyphs also carry an optical correction, because the
// two are not the same thing. Both packs draw on the 24 unit grid and each
// glyph fills a different amount of it, so nine marks at one step paint
// between 15.7 and 21.7 units across. What is read is not the bounding box:
// a closed outline is read at its full extent and an open one at its parts,
// which is why the case and the ring are picked out of the sidebar while the
// route and the gear, measuring the same, are not. So `icon.optical` in the
// token source names the handful of glyphs that are drawn a fraction under
// their step, the box does not move, and the stroke is divided by the same
// fraction so the weight does not either.
//
// Legacy sizing still works: a numeric `width`/`height` is drawn as asked and
// takes the stroke of the nearest step; `fontSize` lets a glyph follow its
// text, in which case no size is known and the pack's own stroke stands. With
// nothing given the glyph is the reference step, 20px.
//
// A call site that sizes the glyph inside its own `sx` keeps both the size and
// the pack's stroke. The wrapper cannot state a weight for a size it does not
// know: it would otherwise hand a 16px glyph the stroke computed for the 20px
// default, which renders 0.96px where the token says 1.3. Those call sites are
// the ones the size sweep converts, and until then they draw exactly what they
// drew before this wrapper existed.
//
// The stroke rule is harmless on a filled glyph: a path with no stroke has
// nothing for the width to apply to.
// ----------------------------------------------------------------------

/** The `sx` keys that decide how large a glyph is drawn. */
const SX_SIZE_KEYS = ['width', 'height', 'fontSize'] as const

/**
 * Whether a call site sizes the glyph through `sx` rather than through a prop.
 *
 * Only plain objects are read, which is every case in the app; a function
 * `sx` is resolved against the theme at render time and cannot be inspected
 * here, so it keeps the wrapper's own sizing rather than silently losing the
 * stroke rule.
 */
export const sxDeclaresSize = (sx: SxProps<Theme> | undefined): boolean =>
  (Array.isArray(sx) ? sx : [sx]).some(
    (entry) =>
      typeof entry === 'object' &&
      entry !== null &&
      SX_SIZE_KEYS.some((key) => key in (entry as Record<string, unknown>)),
  )

type Props = BoxProps &
  IconProps & {
    /**
     * An optical step by name, or a pixel size to snap onto the grid.
     * Takes precedence over `width`, `height` and `fontSize`.
     */
    size?: IconSizeName | number
    /** The glyph is the only content of its control. */
    solo?: boolean
  }

export default function Iconify({
  size,
  solo,
  width,
  height,
  fontSize,
  sx,
  ...other
}: Props) {
  // With nothing given the glyph is the reference step. Resolving it by name
  // rather than by its pixel value keeps the default on the same path as an
  // explicit `size`, stroke rule included.
  const followsText = fontSize !== undefined
  const sizedByProp =
    size !== undefined || width !== undefined || height !== undefined
  // The call site owns the size, so it owns the weight too.
  const sizedBySx = !sizedByProp && !followsText && sxDeclaresSize(sx)
  const unsized = !sizedByProp && !followsText && !sizedBySx
  const geometry = resolveIconGeometry({
    size: size ?? (unsized ? DEFAULT_ICON_SIZE : undefined),
    width,
    height,
    solo,
    glyph: typeof other.icon === 'string' ? other.icon : undefined,
  })

  // Emitted before the caller's `sx`, so a call site can still override any
  // of it. The rule targets every descendant: all three packs group their
  // paths under a `<g stroke-width="2">`, and a CSS declaration beats a
  // presentation attribute on the group as well as on any path that restates
  // it.
  //
  // There is no second guard for `sizedBySx` here: it is what keeps `unsized`
  // false, so the geometry above is resolved with no size at all and reports
  // no stroke. One mechanism, so a test that breaks it fails.
  const stroke: SxProps<Theme> =
    geometry.strokeUnits === undefined
      ? {}
      : { '& *': { strokeWidth: geometry.strokeUnits } }

  // The optical correction, for the handful of glyphs that carry one. A
  // transform and not a smaller box: the box is what the row, the label
  // baseline and the hit target are built on, and the defect being fixed is
  // that a mark reads large inside a box that is already the right size. So
  // the box stays at the step and the drawing inside it comes down, about its
  // own centre. The stroke `geometry` reports is already divided by the same
  // fraction, which the transform multiplies straight back out, so a corrected
  // mark is drawn smaller at exactly the weight the ramp gives its step.
  const optical: SxProps<Theme> =
    geometry.opticalScale === 1
      ? {}
      : {
          transform: `scale(${geometry.opticalScale})`,
          transformOrigin: '50% 50%',
        }

  // A box with a stated size keeps it in a flex row. A glyph is a flex item
  // beside its label almost everywhere, and a flex item shrinks by default,
  // so the moment the label wrapped the mark gave up width to it: a check at
  // the head of a two-line list item drew smaller than the one above it, and
  // the text beside each started at a different x.
  let box: SxProps<Theme>
  if (sizedBySx) {
    box = {}
  } else if (size !== undefined || unsized) {
    box = { width: geometry.px, height: geometry.px, flexShrink: 0 }
  } else if (followsText) {
    box = { fontSize }
  } else {
    box = { width: width ?? height, height: height ?? width, flexShrink: 0 }
  }

  // The wrapper's own declarations, then the call site's, so a screen can
  // still override any of it.
  const own = { ...box, ...stroke, ...optical } as SxProps<Theme>
  const caller = Array.isArray(sx) ? sx : [sx]

  // A drawn glyph takes the same geometry and skips the fetch.
  //
  // The 1em is first in the array rather than a `width` attribute, and that is
  // not cosmetic: `Box` reads `width` and `height` as system props and turns
  // them into CSS, so writing them as attributes puts the same declaration in
  // twice and never reaches the element. First in the array it is what
  // `@iconify/react` writes and what everything after it overrides, the size
  // a glyph takes when the call site has not named one.
  //
  // `role` and `aria-hidden` match the pack component too: the control around
  // the glyph carries the name, so the mark stays out of the accessibility
  // tree. `data-glyph` is what lets the rendered-page audit say which mark it
  // measured rather than "an svg".
  if (typeof other.icon === 'string' && isBeastGlyph(other.icon)) {
    const { icon, ...rest } = other
    return (
      <Box
        component="svg"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-hidden
        focusable="false"
        viewBox={BEAST_GLYPH_VIEW_BOX}
        data-glyph={icon}
        sx={[{ width: '1em', height: '1em' }, own, ...caller]}
        dangerouslySetInnerHTML={{ __html: beastGlyphBody(icon) ?? '' }}
        {...rest}
      />
    )
  }

  // `data-glyph` on this path too, and for the same reason it is on the one
  // above: the ruler that measures a painted page has to be able to say which
  // mark it measured. It is what lets the optical correction be checked
  // against the token source rather than against whatever the component
  // happened to emit, which is a check that cannot fail.
  return (
    <Box
      component={Icon}
      sx={[own, ...caller]}
      data-glyph={typeof other.icon === 'string' ? other.icon : undefined}
      {...other}
    />
  )
}
